import styled from '@emotion/styled';
import type { FunctionComponent } from 'react';
import { Fragment } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import type { SkillSyncStatus } from '~/shared/data-contract/skill';
import { Badge, type BadgeProps } from '~/ui/core/badge/Badge';
import { DropdownMenuItem } from '~/ui/core/dropdown-menu/DropdownMenu';

/** NEEDS_REVIEW is a system-assigned state, so it is never offered as a choice. */
type SelectableSyncStatus = Exclude<SkillSyncStatus, 'NEEDS_REVIEW'>;

const syncStatusOptions: readonly SelectableSyncStatus[] = ['SYNC', 'SYNC_WHEN_IN_PROFILE', 'NOT_SYNCED'];

export const getSyncStatusBadgeVariant = (syncStatus: SkillSyncStatus): 'error' | 'gray' | 'success' | 'warning' => {
    if (syncStatus === 'NOT_SYNCED') return 'error';
    if (syncStatus === 'NEEDS_REVIEW') return 'warning';

    return 'success';
};

type SyncStatusBadgeProps = Omit<BadgeProps, 'children' | 'variant'> & {
    readonly currentStatus: SkillSyncStatus;
};

export const SyncStatusBadge: FunctionComponent<SyncStatusBadgeProps> = ({ className, currentStatus, ...props }) => {
    const translate = useTranslate();

    return (
        <Badge className={className ?? 'max-w-full'} variant={getSyncStatusBadgeVariant(currentStatus)} {...props}>
            <span className="truncate">
                {translate(`pages.job-architecture-v2.skills.sync-status.${currentStatus}.title`)}
            </span>
        </Badge>
    );
};

const SyncStatusOptionItem = styled(DropdownMenuItem)(({ theme }) => ({
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.xs,
    padding: `${theme.spacing.xs} ${theme.spacing.s}`,
}));

const SyncStatusOptionTitle = styled.span(({ theme }) => ({
    color: 'var(--color-text-content-strong)',
    ...theme.typography.body.S.medium,
}));

const SyncStatusOptionExplanation = styled.span(({ theme }) => ({
    color: 'var(--color-text-content-subtle)',
    ...theme.typography.body.S.regular,
}));

type SyncStatusOptionsProps = {
    readonly currentStatus: SkillSyncStatus;
    readonly onStatusChange: (status: SkillSyncStatus) => void;
};

export const SyncStatusOptions: FunctionComponent<SyncStatusOptionsProps> = ({ currentStatus, onStatusChange }) => {
    const translate = useTranslate();

    return (
        <Fragment>
            {syncStatusOptions.map((status) => (
                <SyncStatusOptionItem
                    key={status}
                    data-highlighted={status === currentStatus ? true : undefined}
                    onClick={(e) => {
                        e.stopPropagation();
                        onStatusChange(status);
                    }}
                >
                    <SyncStatusOptionTitle>
                        {translate(`pages.job-architecture-v2.skills.sync-status.${status}.title`)}
                    </SyncStatusOptionTitle>
                    <SyncStatusOptionExplanation>
                        {translate(`pages.job-architecture-v2.skills.sync-status.${status}.explanation`)}
                    </SyncStatusOptionExplanation>
                </SyncStatusOptionItem>
            ))}
        </Fragment>
    );
};
